import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowLeft, ArrowRightLeft, Bot, BotOff, ChevronDown, ChevronUp, Plus,
  Send, StickyNote, Trash2, UserMinus, UserPlus,
} from 'lucide-react';
import { AsyncView, Button, Chip, Dialog, Input, Notice, Select, Textarea, useToast } from '../ui';
import { Segmented } from '../ui/Segmented';
import { api, ApiError } from '../lib/api';
import { podeAtuar, useSessao } from '../lib/session';
import { useConversas, type ConversaResumo, type FiltroFila } from '../lib/useConversas';
import { useResource } from '../lib/useResource';
import './inbox.css';

/** A Meta só aceita mensagem livre dentro de 24h do último recebimento. Fora
 *  dela, apenas modelo aprovado. O servidor já recusava com
 *  `WindowClosedError`; a tela não dizia nada antes de tentar. */
const JANELA_MS = 24 * 60 * 60 * 1000;

const FILTROS: ReadonlyArray<{ id: FiltroFila; rotulo: string }> = [
  { id: 'ALL', rotulo: 'Todas' },
  { id: 'PENDING', rotulo: 'Aguardando' },
  { id: 'OPEN', rotulo: 'Em atendimento' },
  { id: 'MINE', rotulo: 'Minhas' },
  { id: 'RESOLVED', rotulo: 'Resolvidas' },
];

const STATUS: Record<string, { rotulo: string; tom: 'warning' | 'success' | 'neutral' }> = {
  PENDING: { rotulo: 'Aguardando', tom: 'warning' },
  OPEN: { rotulo: 'Em atendimento', tom: 'success' },
  RESOLVED: { rotulo: 'Resolvida', tom: 'neutral' },
};

/** A janela conta do último recebimento. Sem nada recebido, nunca esteve
 *  aberta — que é diferente de "expirou" e pede outra frase na tela. */
const janelaAberta = (ultimoRecebido: string | null) =>
  ultimoRecebido !== null && Date.now() - new Date(ultimoRecebido).getTime() < JANELA_MS;

const hora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

interface Mensagem {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  type: string;
  payload: unknown;
  status: string;
  createdAt: string;
  errorMessage?: string | null;
}
interface Nota { id: string; body: string; createdAt: string; author: { name: string } | null }
interface EventoAtribuicao {
  id: string;
  reason: 'CLAIM' | 'TRANSFER' | 'RELEASE' | 'AUTO_ASSIGNMENT' | string;
  createdAt: string;
  fromAgent: { name: string } | null;
  toAgent: { name: string } | null;
}
interface Detalhe extends ConversaResumo {
  messages: Mensagem[];
  notes: Nota[];
  /** Vinte eventos mais recentes. O endpoint devolvia desde sempre e a tela
   *  jogava fora — o README já prometia "histórico auditável de atribuições,
   *  transferências e liberações", que existia no banco e em lugar nenhum da
   *  interface. */
  assignmentEvents: EventoAtribuicao[];
  olderCursor: string | null;
  /** Campos que o endpoint sempre devolveu e a tela nunca leu. */
  automationPaused: boolean;
  lastInboundAt: string | null;
}
interface Operador { id: string; name: string; isActive: boolean; availability: string }
interface Modelo { id: string; name: string; language: string; status: string }

type ModoEnvio = 'texto' | 'botoes' | 'modelo';

const TIPO_MIDIA: Record<string, string> = {
  IMAGE: 'Imagem', VIDEO: 'Vídeo', AUDIO: 'Áudio', DOCUMENT: 'Documento', STICKER: 'Figurinha', LOCATION: 'Localização',
};

/** Os motivos vêm do enum `AssignmentReason`. Frase completa, não rótulo: numa
 *  linha de histórico "TRANSFER" não diz de quem para quem, e é isso que se
 *  quer saber ao auditar. */
function frase(e: EventoAtribuicao): string {
  const de = e.fromAgent?.name;
  const para = e.toAgent?.name;
  if (e.reason === 'RELEASE') return `${de ?? 'Alguém'} devolveu a conversa para a fila`;
  if (e.reason === 'TRANSFER') return `${de ?? 'Alguém'} transferiu para ${para ?? 'a fila'}`;
  if (e.reason === 'AUTO_ASSIGNMENT') return `Distribuição automática atribuiu a ${para ?? 'ninguém'}`;
  if (e.reason === 'CLAIM') return `${para ?? 'Alguém'} assumiu a conversa`;
  return `${e.reason}: ${de ?? '—'} → ${para ?? '—'}`;
}

const ENTREGA: Record<string, string> = {
  SENT: 'Enviada', DELIVERED: 'Entregue', READ: 'Lida', FAILED: 'Falhou', RECEIVED: 'Recebida', PENDING: 'Enviando',
};

/**
 * Corpo da mensagem.
 *
 * Mídia NÃO é despejada como JSON. O front anterior renderizava
 * `JSON.stringify(payload)` para tudo que não fosse texto, então uma imagem
 * aparecia como `{"image":{"mime_type":"image/jpeg"...}}` no meio da conversa.
 */
function Corpo({ m }: { m: Mensagem }) {
  const p = m.payload as Record<string, { body?: string; caption?: string; filename?: string; mime_type?: string }> & { body?: string };
  if (m.type === 'TEXT') return <>{p.text?.body ?? p.body ?? ''}</>;

  const midia = p[m.type.toLowerCase()] ?? {};
  const rotulo = TIPO_MIDIA[m.type] ?? m.type;
  const nome = midia.filename ?? midia.caption ?? midia.mime_type ?? null;
  return (
    <span className="bolha__midia">
      {rotulo}
      {nome && <span style={{ fontWeight: 'var(--weight-regular)', color: 'var(--text-muted)' }}>· {nome}</span>}
    </span>
  );
}

export function Inbox({initialConversationId=null}:{initialConversationId?:string|null}) {
  const toast = useToast();
  const [filtro, setFiltro] = useState<FiltroFila>('ALL');
  const [aberta, setAberta] = useState<string | null>(initialConversationId);
  const { state, temMais, carregandoMais, carregarMais, recarregar } = useConversas(filtro);

  /* Rascunho por conversa, fora do estado do detalhe.
     A conversa se atualiza sozinha a cada poucos segundos; se o texto morasse
     no mesmo objeto, cada atualização apagaria o que o atendente digitou. Era
     o bug mais irritante do front anterior e o único que a suíte de
     caracterização trava explicitamente. */
  const rascunhos = useRef<Map<string, string>>(new Map());
  const [rascunho, setRascunho] = useState('');
  const [enviando, setEnviando] = useState(false);

  const abrir = useCallback((id: string) => {
    setAberta(id);
    setRascunho(rascunhos.current.get(id) ?? '');
  }, []);

  useEffect(()=>{if(initialConversationId)abrir(initialConversationId);},[initialConversationId,abrir]);

  useEffect(() => {
    if (aberta) rascunhos.current.set(aberta, rascunho);
  }, [aberta, rascunho]);

  return (
    <div className="inbox" data-aberta={aberta !== null}>
      <section className="inbox__lista" aria-label="Fila de conversas">
        {/* `aria-pressed` e não `aria-selected`: são botões de alternância, não
            uma lista de opções — e um leitor de tela precisa saber qual filtro
            está ativo sem depender do fundo colorido. */}
        <div className="filtros" role="group" aria-label="Filtrar fila">
          {FILTROS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="filtro"
              aria-pressed={filtro === f.id}
              onClick={() => setFiltro(f.id)}
            >
              {f.rotulo}
            </button>
          ))}
        </div>

        <AsyncView
          state={state}
          label="as conversas"
          skeletonRows={6}
          empty={{
            title: 'Nada nesta fila',
            text: filtro === 'ALL'
              ? 'Quando alguém escrever para o seu número, a conversa aparece aqui.'
              : 'Nenhuma conversa corresponde a este filtro. Tente "Todas".',
          }}
        >
          {(conversas) => (
            <>
            <ul className="conversas">
              {conversas.map((c) => {
                const s = STATUS[c.status] ?? { rotulo: c.status, tom: 'neutral' as const };
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="conversa"
                      aria-current={aberta === c.id}
                      onClick={() => abrir(c.id)}
                    >
                      <span className="conversa__nome">{c.contact.displayName ?? c.contact.profileName ?? c.contact.waId}</span>
                      <span className="conversa__quando">{hora(c.lastMessageAt)}</span>
                      {c.lastMessage && (
                        <span className="conversa__previa">
                          {/* Prefixo textual: sem ele, prévia de mensagem
                              recebida e enviada são indistinguíveis. */}
                          {c.lastMessage.direction === 'OUTBOUND' ? 'Você: ' : ''}
                          {c.lastMessage.preview}
                        </span>
                      )}
                      <span className="conversa__meta">
                        <Chip tone={s.tom}>{s.rotulo}</Chip>
                        <Chip>{c.agent ? c.agent.name : 'Sem responsável'}</Chip>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {/* A fila não cabe numa página, e antes disso não havia nem sinal
                de que existia mais nada: o `nextCursor` chegava e era
                descartado. Um botão explícito, e não rolagem infinita — a lista
                se reordena sozinha a cada atualização, e rolagem infinita sobre
                conteúdo que se move é como se perde o lugar. */}
            {temMais && (
              <div className="fila__mais">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={carregandoMais}
                  loadingLabel="Carregando mais conversas"
                  icon={<ChevronDown size={16} aria-hidden="true" />}
                  onClick={() => void carregarMais()}
                >
                  Carregar mais conversas
                </Button>
              </div>
            )}
            </>
          )}
        </AsyncView>
      </section>

      {aberta ? (
        <Painel
          id={aberta}
          rascunho={rascunho}
          setRascunho={setRascunho}
          enviando={enviando}
          setEnviando={setEnviando}
          onVoltar={() => setAberta(null)}
          onMudou={recarregar}
          aviso={toast}
        />
      ) : (
        <section className="painel" aria-label="Conversa">
          <div className="state">
            <p className="state__title">Nenhuma conversa aberta</p>
            <p className="state__text">Escolha uma conversa na fila ao lado para ver o histórico e responder.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function Painel({
  id, rascunho, setRascunho, enviando, setEnviando, onVoltar, onMudou, aviso,
}: {
  id: string;
  rascunho: string;
  setRascunho: (v: string) => void;
  enviando: boolean;
  setEnviando: (v: boolean) => void;
  onVoltar: () => void;
  onMudou: () => void;
  aviso: (m: string, t?: 'neutral' | 'danger') => void;
}) {
  const { state, recarregar } = useResource<Detalhe>(`/workspace/conversations/${id}`, { vazio: () => false });
  const fim = useRef<HTMLDivElement>(null);
  const titulo = useRef<HTMLHeadingElement>(null);
  const caixa = useRef<HTMLDivElement>(null);

  /* Histórico anterior.
     O detalhe devolve as mensagens mais recentes e um `olderCursor`, e a versão
     anterior desta tela usava esse cursor apenas para escrever "Mostrando as
     mensagens mais recentes" — um aviso de que havia mais, sem forma de chegar
     lá. Numa conversa longa, o que veio antes simplesmente não existia.

     `cursorAntigo` tem três estados de propósito: `undefined` é "ainda não
     paginei, use o cursor do servidor"; uma string é "próximo trecho"; `null` é
     "acabou". Com dois estados, o `null` do fim voltaria a cair no cursor
     original e o botão nunca sumiria. */
  const [antigas, setAntigas] = useState<Mensagem[]>([]);
  const [cursorAntigo, setCursorAntigo] = useState<string | null | undefined>(undefined);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const alturaAntes = useRef<number | null>(null);

  /* Atribuição, automação e nota interna.
     Os três já existiam no servidor: `PATCH /conversations/:id` aceita
     `assignedTo` e `automationPaused` (e a tela mandava só `status`), e
     `POST /conversations/:id/notes` nunca teve campo. */
  const { agent: eu } = useSessao();
  const podeTransferir = podeAtuar(eu.role, 'SUPERVISOR');
  const [transferindo, setTransferindo] = useState(false);
  const [destino, setDestino] = useState('');
  const [mexendo, setMexendo] = useState(false);
  const [nota, setNota] = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  const [modo, setModo] = useState<ModoEnvio>('texto');
  const [corpoBotoes, setCorpoBotoes] = useState('');
  const [rodape, setRodape] = useState('');
  const [botoes, setBotoes] = useState<Array<{ id: string; title: string }>>([{ id: 'opcao_1', title: '' }]);
  const [modeloId, setModeloId] = useState('');

  // Só quem pode transferir precisa da lista de operadores.
  const operadores = useResource<Operador[]>('/workspace/agents', {
    enabled: podeTransferir && transferindo,
    vazio: () => false,
  });
  // Modelos só quando o modo pede — é a lista mais cara das três.
  const modelos = useResource<Modelo[]>('/workspace/templates', {
    enabled: modo === 'modelo',
    vazio: () => false,
  });

  /**
   * Muda o responsável.
   *
   * O 409 aqui não é erro genérico: significa que outro operador assumiu entre
   * a leitura da tela e o clique. Recarregar junto com o aviso é o que impede
   * a pessoa de continuar olhando um estado que já não existe e tentar de novo
   * no mesmo botão.
   */
  async function atribuir(paraId: string | null, frase: string) {
    setMexendo(true);
    try {
      await api(`/workspace/conversations/${id}`, { method: 'PATCH', body: { assignedTo: paraId } });
      recarregar();
      onMudou();
      aviso(frase);
      setTransferindo(false);
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 409) {
        recarregar();
        onMudou();
        aviso(erro.message, 'danger');
      } else {
        aviso(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o responsável.', 'danger');
      }
    } finally {
      setMexendo(false);
    }
  }

  async function alternarAutomacao(pausada: boolean) {
    setMexendo(true);
    try {
      await api(`/workspace/conversations/${id}`, { method: 'PATCH', body: { automationPaused: !pausada } });
      recarregar();
      aviso(pausada ? 'Automação retomada nesta conversa.' : 'Automação pausada nesta conversa.');
    } catch (erro) {
      aviso(erro instanceof ApiError ? erro.message : 'Não foi possível alterar a automação.', 'danger');
    } finally {
      setMexendo(false);
    }
  }

  async function salvarNota(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const texto = nota.trim();
    if (!texto) return;
    setSalvandoNota(true);
    try {
      await api(`/workspace/conversations/${id}/notes`, { method: 'POST', body: { body: texto } });
      setNota('');
      recarregar();
      aviso('Nota interna salva.');
    } catch (erro) {
      // Igual ao rascunho de resposta: o texto NÃO é apagado no erro.
      aviso(erro instanceof ApiError ? erro.message : 'Não foi possível salvar a nota.', 'danger');
    } finally {
      setSalvandoNota(false);
    }
  }

  useEffect(() => { setAntigas([]); setCursorAntigo(undefined); }, [id]);

  /* Prender a rolagem no lugar.
     Inserir conteúdo ACIMA do que está visível empurra tudo para baixo: sem
     isto, clicar em "Carregar anteriores" joga a pessoa para um ponto
     arbitrário do histórico. `useLayoutEffect` porque a correção precisa
     acontecer antes da pintura — em `useEffect` o salto é visível. */
  useLayoutEffect(() => {
    const node = caixa.current;
    if (!node || alturaAntes.current === null) return;
    node.scrollTop += node.scrollHeight - alturaAntes.current;
    alturaAntes.current = null;
  }, [antigas]);

  /* Abrir uma conversa move o foco para o título dela. Sem isso, no telefone o
     foco fica no item da lista que acabou de sair da tela.

     A dependência é [id, state.status] e não [id]: o painel monta em `loading`,
     e nesse instante o <h2> ainda não existe — a primeira versão chamava
     `focus()` num ref nulo e não movia nada. Medido: o foco continuava no botão
     da lista. */
  const pronto = state.status === 'ready';
  useEffect(() => { if (pronto) titulo.current?.focus(); }, [id, pronto]);
  useEffect(() => { if (pronto) fim.current?.scrollIntoView({ block: 'end' }); }, [pronto, state]);

  const dados = state.status === 'ready' ? state.data : null;
  const cursor = cursorAntigo === undefined ? (dados?.olderCursor ?? null) : cursorAntigo;

  async function carregarAntigas() {
    if (!cursor || carregandoAntigas) return;
    setCarregandoAntigas(true);
    alturaAntes.current = caixa.current?.scrollHeight ?? null;
    try {
      const trecho = await api<Detalhe>(
        `/workspace/conversations/${id}?before=${encodeURIComponent(cursor)}`,
      );
      setAntigas((atuais) => {
        const conhecidas = new Set(atuais.map((m) => m.id));
        return [...trecho.messages.filter((m) => !conhecidas.has(m.id)), ...atuais];
      });
      setCursorAntigo(trecho.olderCursor);
    } catch (erro) {
      alturaAntes.current = null;
      aviso(erro instanceof ApiError ? erro.message : 'Não foi possível carregar o histórico.', 'danger');
    } finally {
      setCarregandoAntigas(false);
    }
  }

  /**
   * Envio.
   *
   * `messageSchema` aceita três tipos desde sempre — texto, botões e modelo —
   * e o compositor mandava só texto. Modelo é o único que atravessa a janela de
   * 24 horas fechada, então sem ele a tela simplesmente não conseguia falar com
   * quem parou de responder ontem.
   */
  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    let corpo: Record<string, unknown>;
    if (modo === 'texto') {
      const texto = rascunho.trim();
      if (!texto) return;
      corpo = { kind: 'text', body: texto };
    } else if (modo === 'botoes') {
      const texto = corpoBotoes.trim();
      const validos = botoes.filter((b) => b.title.trim());
      if (!texto) { aviso('Escreva a mensagem que acompanha os botões.', 'danger'); return; }
      if (!validos.length) { aviso('Adicione ao menos um botão com rótulo.', 'danger'); return; }
      if (validos.some((b) => !/^[A-Za-z0-9_.:-]+$/.test(b.id))) {
        aviso('O identificador do botão aceita apenas letras, números, ponto, hífen, dois-pontos e sublinhado.', 'danger');
        return;
      }
      corpo = {
        kind: 'interactive_buttons',
        body: texto,
        ...(rodape.trim() ? { footer: rodape.trim() } : {}),
        buttons: validos.map((b) => ({ id: b.id, title: b.title.trim() })),
      };
    } else {
      const escolhido = (modelos.state.status === 'ready' ? modelos.state.data : []).find((m) => m.id === modeloId);
      if (!escolhido) { aviso('Escolha um modelo aprovado.', 'danger'); return; }
      corpo = { kind: 'template', name: escolhido.name, language: escolhido.language };
    }
    setEnviando(true);
    try {
      await api(`/workspace/conversations/${id}/messages`, { method: 'POST', body: corpo });
      if (modo === 'texto') setRascunho('');
      if (modo === 'botoes') { setCorpoBotoes(''); setRodape(''); setBotoes([{ id: 'opcao_1', title: '' }]); }
      recarregar();
      onMudou();
    } catch (erro) {
      // O rascunho NÃO é limpo no erro: apagar o texto de quem acabou de
      // escrever uma resposta longa é perder trabalho por causa de uma falha
      // de rede.
      aviso(erro instanceof ApiError ? erro.message : 'Não foi possível enviar.', 'danger');
    } finally {
      setEnviando(false);
    }
  }

  async function mudarStatus(status: 'OPEN' | 'RESOLVED') {
    try {
      await api(`/workspace/conversations/${id}`, { method: 'PATCH', body: { status } });
      recarregar();
      onMudou();
      aviso(status === 'RESOLVED' ? 'Conversa resolvida.' : 'Conversa reaberta.');
    } catch (erro) {
      aviso(erro instanceof ApiError ? erro.message : 'Não foi possível atualizar.', 'danger');
    }
  }

  return (
    <section className="painel" aria-label="Conversa">
      <AsyncView
        state={state}
        label="a conversa"
        skeletonRows={4}
        empty={{ title: 'Conversa vazia', text: 'Nenhuma mensagem trocada ainda.' }}
      >
        {(c) => (
          <>
            <header className="painel__topo">
              <div className="painel__quem">
                <h2 ref={titulo} tabIndex={-1} data-focus-silent style={{ fontSize: 'var(--text-lg)' }}>
                  {c.contact.displayName ?? c.contact.profileName ?? c.contact.waId}
                </h2>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {c.contact.waId} · por {c.phoneNumber.displayNumber}
                </span>
                {c.contact.crmContactId && (
                  <span style={{ fontSize:'var(--text-xs)', color:'var(--text-muted)' }}>
                    {[c.contact.crmJobTitle,c.contact.crmOrganizations?.map((item)=>item.name).join(' · ') || c.contact.crmAccountName].filter(Boolean).join(' · ') || 'Contato sincronizado com o Lead Machine'}
                  </span>
                )}
              </div>
              <div className="painel__acoes">
                <span className="painel__voltar">
                  <Button variant="tertiary" size="sm" onClick={onVoltar} icon={<ArrowLeft size={16} aria-hidden="true" />}>
                    Voltar à fila
                  </Button>
                </span>
                {c.status === 'RESOLVED' ? (
                  <Button variant="secondary" size="sm" onClick={() => mudarStatus('OPEN')}>Reabrir</Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => mudarStatus('RESOLVED')}>Marcar resolvida</Button>
                )}
              </div>
            </header>

            {/* Barra de atribuição.
                Toda a mecânica de fila — assumir, transferir, liberar, com
                registro em `AssignmentEvent` e 409 quando dois operadores
                clicam ao mesmo tempo — existia no servidor e não tinha um
                único botão. Sem ela, "Minhas" era um filtro sobre um campo que
                a interface nunca conseguia mudar. */}
            <div className="atribuicao">
              <span className="atribuicao__quem">
                {c.agent
                  ? <>Responsável: <strong>{c.agent.id === eu.id ? 'você' : c.agent.name}</strong></>
                  : 'Sem responsável'}
              </span>
              <div className="atribuicao__botoes">
                {(!c.assignedTo || (c.assignedTo !== eu.id && podeTransferir)) && (
                  <Button
                    variant="secondary" size="sm" loading={mexendo}
                    icon={<UserPlus size={16} aria-hidden="true" />}
                    onClick={() => void atribuir(eu.id, 'Conversa assumida por você.')}
                  >
                    Assumir
                  </Button>
                )}
                {c.assignedTo && (c.assignedTo === eu.id || podeTransferir) && (
                  <Button
                    variant="tertiary" size="sm" loading={mexendo}
                    icon={<UserMinus size={16} aria-hidden="true" />}
                    onClick={() => void atribuir(null, 'Conversa devolvida para a fila.')}
                  >
                    Liberar
                  </Button>
                )}
                {podeTransferir && (
                  <Button
                    variant="tertiary" size="sm"
                    icon={<ArrowRightLeft size={16} aria-hidden="true" />}
                    onClick={() => { setDestino(''); setTransferindo(true); }}
                  >
                    Transferir
                  </Button>
                )}
                {/* Pausar a automação é o controle que falta quando um humano
                    assume: o robô continuar respondendo por cima é o pior
                    cenário de atendimento assistido. */}
                <Button
                  variant="tertiary" size="sm" loading={mexendo}
                  icon={c.automationPaused
                    ? <Bot size={16} aria-hidden="true" />
                    : <BotOff size={16} aria-hidden="true" />}
                  onClick={() => void alternarAutomacao(c.automationPaused)}
                >
                  {c.automationPaused ? 'Retomar automação' : 'Pausar automação'}
                </Button>
                {c.automationPaused && <Chip tone="warning">Automação pausada</Chip>}
              </div>
            </div>

            <Dialog
              open={transferindo}
              onClose={() => setTransferindo(false)}
              title="Transferir conversa"
              description="O operador escolhido passa a ser o responsável. A troca fica registrada no histórico de atribuição."
              footer={
                <>
                  <Button variant="secondary" onClick={() => setTransferindo(false)}>Cancelar</Button>
                  <Button
                    variant="primary" loading={mexendo} disabled={!destino}
                    icon={<ArrowRightLeft size={18} aria-hidden="true" />}
                    onClick={() => void atribuir(destino, 'Conversa transferida.')}
                  >
                    Transferir
                  </Button>
                </>
              }
            >
              <Select
                label="Transferir para"
                value={destino}
                placeholder="Selecione o operador"
                /* Só operadores ativos: o servidor recusa os demais com "o
                   operador selecionado não está ativo", e oferecer uma opção
                   que sempre falha é desperdiçar o clique. */
                options={(operadores.state.status === 'ready' ? operadores.state.data : [])
                  .filter((o) => o.isActive && o.id !== c.assignedTo)
                  .map((o) => ({ value: o.id, label: o.id === eu.id ? `${o.name} (você)` : o.name }))}
                onChange={(e) => setDestino(e.currentTarget.value)}
              />
            </Dialog>

            <div className="mensagens" role="log" aria-label="Histórico da conversa" ref={caixa}>
              {cursor ? (
                <div className="mensagens__mais">
                  <Button
                    variant="tertiary"
                    size="sm"
                    loading={carregandoAntigas}
                    loadingLabel="Carregando o histórico anterior"
                    icon={<ChevronUp size={16} aria-hidden="true" />}
                    onClick={() => void carregarAntigas()}
                  >
                    Carregar mensagens anteriores
                  </Button>
                </div>
              ) : antigas.length > 0 ? (
                <p className="mensagens__inicio">Início da conversa.</p>
              ) : null}
              {[...antigas, ...c.messages].map((m) => (
                <div key={m.id} className={`bolha bolha--${m.direction === 'INBOUND' ? 'in' : 'out'}`}>
                  {/* Direção em texto, dentro da bolha. Alinhamento e cor não
                      existem para quem usa leitor de tela. */}
                  <span className="sr-only">{m.direction === 'INBOUND' ? 'Recebida:' : 'Enviada:'}</span>
                  <Corpo m={m} />
                  <div className="bolha__rodape">
                    <span>{hora(m.createdAt)}</span>
                    <span>{ENTREGA[m.status] ?? m.status}</span>
                  </div>
                  {m.errorMessage && (
                    <Notice tone="danger" live>{m.errorMessage}</Notice>
                  )}
                </div>
              ))}
              <div ref={fim} />
            </div>

            <form className="composer" onSubmit={enviar}>
              <Segmented
                rotuloGrupo="Tipo de mensagem"
                valor={modo}
                onChange={(v) => setModo(v as ModoEnvio)}
                opcoes={[
                  { valor: 'texto', rotulo: 'Texto' },
                  { valor: 'botoes', rotulo: 'Botões' },
                  { valor: 'modelo', rotulo: 'Modelo' },
                ]}
              />

              {/* A janela de 24 horas é regra da Meta e o servidor já a aplicava
                  com `WindowClosedError`. A tela não dizia nada: você escrevia
                  a resposta inteira para descobrir no envio que ela não sairia.
                  Agora o estado aparece antes de digitar, com a saída. */}
              {!janelaAberta(c.lastInboundAt) && modo !== 'modelo' && (
                <Notice tone="warning" title="Janela de 24 horas fechada">
                  {c.lastInboundAt
                    ? 'O contato não escreve há mais de 24 horas.'
                    : 'Este contato nunca escreveu para você.'}{' '}
                  Fora da janela, a Meta só aceita modelo aprovado — troque para “Modelo”.
                </Notice>
              )}

              {modo === 'texto' && (
                <Textarea
                  label="Responder"
                  name="resposta"
                  rows={3}
                  value={rascunho}
                  onChange={(e) => setRascunho(e.currentTarget.value)}
                  hint="Ctrl+Enter envia."
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') e.currentTarget.form?.requestSubmit();
                  }}
                />
              )}

              {modo === 'botoes' && (
                <>
                  <Textarea label="Mensagem" rows={2} maxLength={1024} value={corpoBotoes}
                    onChange={(e) => setCorpoBotoes(e.currentTarget.value)} />
                  <Input label="Rodapé" maxLength={60} value={rodape}
                    onChange={(e) => setRodape(e.currentTarget.value)}
                    hint="Opcional. Até 60 caracteres." />
                  {botoes.map((botao, i) => (
                    <div className="composer__botao" key={i}>
                      <Input label={`Botão ${i + 1}`} maxLength={20} value={botao.title}
                        onChange={(e) => setBotoes(botoes.map((b, k) => (k === i ? { ...b, title: e.currentTarget.value } : b)))}
                        hint="O WhatsApp corta em 20 caracteres." />
                      <Input label={`Identificador ${i + 1}`} value={botao.id}
                        onChange={(e) => setBotoes(botoes.map((b, k) => (k === i ? { ...b, id: e.currentTarget.value } : b)))}
                        hint="Volta na resposta do contato." />
                      {botoes.length > 1 && (
                        <Button variant="danger-quiet" size="sm" icon={<Trash2 size={16} aria-hidden="true" />}
                          onClick={() => setBotoes(botoes.filter((_, k) => k !== i))}>
                          Remover botão {i + 1}
                        </Button>
                      )}
                    </div>
                  ))}
                  {botoes.length < 3 && (
                    <Button variant="secondary" size="sm" icon={<Plus size={16} aria-hidden="true" />}
                      onClick={() => setBotoes([...botoes, { id: `opcao_${botoes.length + 1}`, title: '' }])}>
                      Adicionar botão
                    </Button>
                  )}
                </>
              )}

              {modo === 'modelo' && (
                <Select
                  label="Modelo aprovado"
                  value={modeloId}
                  placeholder="Selecione"
                  options={(modelos.state.status === 'ready' ? modelos.state.data : [])
                    .filter((m) => m.status === 'APPROVED')
                    .map((m) => ({ value: m.id, label: `${m.name} · ${m.language}` }))}
                  onChange={(e) => setModeloId(e.currentTarget.value)}
                  hint="Modelo é o único tipo que a Meta aceita fora da janela de 24 horas."
                />
              )}

              <div className="composer__linha">
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {c.notes.length === 0
                    ? 'Sem notas internas'
                    : c.notes.length === 1
                      ? '1 nota interna'
                      : `${c.notes.length} notas internas`}
                </span>
                <Button
                  variant="primary"
                  type="submit"
                  loading={enviando}
                  disabled={modo === 'texto' ? !rascunho.trim() : modo === 'modelo' ? !modeloId : !corpoBotoes.trim()}
                  icon={<Send size={18} aria-hidden="true" />}
                >
                  Enviar
                </Button>
              </div>
            </form>

            {/* Nota interna.
                A tela EXIBIA as notas e contava quantas eram, sem nenhum campo
                para criar — ler o que não se pode escrever denuncia sozinho o
                corte. O formulário fica aqui embaixo, com o mesmo fundo das
                notas e separado do compositor de resposta de propósito: campo
                único com duas destinações é como se manda para o cliente um
                texto escrito para a equipe. */}
            <div className="notas">
              <form className="nota-form" onSubmit={salvarNota}>
                <Textarea
                  label="Nota interna"
                  rows={2}
                  maxLength={4000}
                  value={nota}
                  onChange={(e) => setNota(e.currentTarget.value)}
                  hint="Só a equipe vê. O contato não recebe nada."
                />
                <Button
                  variant="secondary" type="submit" size="sm"
                  loading={salvandoNota} loadingLabel="Salvando a nota"
                  disabled={!nota.trim()}
                  icon={<StickyNote size={16} aria-hidden="true" />}
                >
                  Salvar nota
                </Button>
              </form>

              {c.notes.map((n) => (
                <div key={n.id} className="nota">
                  {n.body}
                  <p className="nota__quem">{n.author?.name ?? 'Sistema'} · {hora(n.createdAt)}</p>
                </div>
              ))}
            </div>

            {/* Histórico de atribuição.
                Fechado por padrão: é informação de auditoria, consultada quando
                alguém pergunta "quem estava com isso", não a cada abertura da
                conversa. Um <details> nativo entrega abrir/fechar, teclado e
                anúncio de estado sem nenhum script. */}
            {c.assignmentEvents.length > 0 && (
              <details className="historico">
                <summary className="historico__titulo">
                  Histórico de atribuição ({c.assignmentEvents.length})
                </summary>
                <ol className="historico__lista">
                  {c.assignmentEvents.map((e) => (
                    <li key={e.id}>
                      {frase(e)} <span className="historico__quando">· {hora(e.createdAt)}</span>
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </>
        )}
      </AsyncView>
    </section>
  );
}
